import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Divider,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  TextField,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from 'store/hook';
import {
  convertKpaToPsi,
  convertCentToFahr,
  convertPsiToKpa,
  convertFahrToCent,
} from 'utils/helpers/unitSystem';
import TireProfileHandler from 'handlers/tire-pressure';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { TireStatus } from 'utils/tpms';
import FourWheels from 'views/DriverBehavior/Inclinometer/Schematics/FourWheels';
import SixWheels from 'views/DriverBehavior/Inclinometer/Schematics/SixWheels';
import ThreeAxlesSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/ThreeAxlesSixWheels';
import ThreeAxlesTenWheels from 'views/DriverBehavior/Inclinometer/Schematics/ThreeAxlesTenWheels';
import FourSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/FourSixWheels';
import { useTranslation } from 'react-i18next';

export interface AxleWiseThresholdsModalProps {
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  customerId: string;
  saveAxleLimit: (payload: any) => Promise<boolean>;
  initialAssetId?: string;
}

export const AxleWiseThresholdsModal = ({
  open,
  handleAbort,
  enableDarkTheme,
  customerId,
  saveAxleLimit,
  initialAssetId,
}: AxleWiseThresholdsModalProps) => {
  const { t } = useTranslation();
  const handler = new TireProfileHandler();
  const {
    customer: { unitSystem },
  } = useAppSelector((state) => state.authReducer);
  const { assets } = useAppSelector((state) => state.assetReducer);

  const [data, setData] = useState<any[]>([]);
  const [selectedMakeModelPath, setSelectedMakeModelPath] =
    useState<string>('');
  const [axleForms, setAxleForms] = useState<Record<number, any>>({});
  const [originalAxleForms, setOriginalAxleForms] = useState<
    Record<number, any>
  >({});

  // Per-asset threshold controls
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [applyToAll, setApplyToAll] = useState<boolean>(true);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    nextAction: () => void;
  }>({
    open: false,
    nextAction: () => {},
  });

  const fetchLimits = async (assetId?: string) => {
    const res = await handler.getAxleWiseLimits(customerId, assetId);
    if (res && res.length > 0) {
      setData(res);
      if (!selectedMakeModelPath) {
        handleSelectMakeModel(res[0], res);
      } else {
        const item = res.find(
          (r: any) => `${r.make}-${r.model}` === selectedMakeModelPath
        );
        if (item) {
          handleSelectMakeModel(item, res);
        }
      }
    }
  };

  useEffect(() => {
    if (open) {
      const initAsset = initialAssetId ?? '';
      setSelectedAssetId(initAsset);
      setApplyToAll(!initAsset);
      void fetchLimits(initAsset || undefined);
    }
  }, [open, customerId]);

  useEffect(() => {
    // Check if there are unsaved changes
    const isChanged = Object.keys(axleForms).some((axleNoStr) => {
      const axleNo = Number(axleNoStr);
      const current = axleForms[axleNo];
      const original = originalAxleForms[axleNo];
      if (!current || !original) return false;
      return (
        current.minPressure !== original.minPressure ||
        current.maxPressure !== original.maxPressure ||
        current.warningPressure !== original.warningPressure ||
        current.minTemperature !== original.minTemperature ||
        current.maxTemperature !== original.maxTemperature ||
        current.warningTemperature !== original.warningTemperature
      );
    });
    setHasUnsavedChanges(isChanged);
  }, [axleForms, originalAxleForms]);

  const handleSelectMakeModel = (item: any, allData: any[] = data) => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        open: true,
        nextAction: () => {
          applyMakeModelSelection(item, allData);
          setConfirmDialog({ open: false, nextAction: () => {} });
        },
      });
    } else {
      applyMakeModelSelection(item, allData);
    }
  };

  const applyMakeModelSelection = (item: any, allData: any[]) => {
    const path = `${item.make}-${item.model}`;
    setSelectedMakeModelPath(path);

    // Initialize form states for the axles
    const newAxleForms: Record<number, any> = {};
    const newOriginalAxleForms: Record<number, any> = {};

    const axlesGrouped = item.positions.reduce((acc: any, curr: any) => {
      if (!acc[curr.axleNo]) acc[curr.axleNo] = [];
      acc[curr.axleNo].push(curr);
      return acc;
    }, {});

    Object.keys(axlesGrouped).forEach((axleNoStr) => {
      const axleNo = Number(axleNoStr);
      const positions = axlesGrouped[axleNo];
      const rep = positions[0]; // Take representative using first tire position

      const formState = {
        minPressure: Math.round(
          unitSystem !== 'metric' && rep.minPressure
            ? convertKpaToPsi(rep.minPressure)
            : rep.minPressure || 0
        ).toString(),
        maxPressure: Math.round(
          unitSystem !== 'metric' && rep.maxPressure
            ? convertKpaToPsi(rep.maxPressure)
            : rep.maxPressure || 0
        ).toString(),
        warningPressure: Math.round(
          unitSystem !== 'metric' && rep.warningPressure
            ? convertKpaToPsi(rep.warningPressure)
            : rep.warningPressure || 0
        ).toString(),
        minTemperature: Math.round(
          unitSystem !== 'metric' && rep.minTemperature
            ? convertCentToFahr(rep.minTemperature)
            : rep.minTemperature || 0
        ).toString(),
        maxTemperature: Math.round(
          unitSystem !== 'metric' && rep.maxTemperature
            ? convertCentToFahr(rep.maxTemperature)
            : rep.maxTemperature || 0
        ).toString(),
        warningTemperature: Math.round(
          unitSystem !== 'metric' && rep.warningTemperature
            ? convertCentToFahr(rep.warningTemperature)
            : rep.warningTemperature || 0
        ).toString(),
      };

      newAxleForms[axleNo] = { ...formState };
      newOriginalAxleForms[axleNo] = { ...formState };
    });

    setAxleForms(newAxleForms);
    setOriginalAxleForms(newOriginalAxleForms);
    setHasUnsavedChanges(false);
  };

  const attemptClose = () => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        open: true,
        nextAction: () => {
          handleAbort();
          setConfirmDialog({ open: false, nextAction: () => {} });
        },
      });
    } else {
      handleAbort();
    }
  };

  const [errors, setErrors] = useState<Record<number, Record<string, string>>>(
    {}
  );

  const handleFieldChange = (axleNo: number, field: string, value: string) => {
    setAxleForms((prev) => ({
      ...prev,
      [axleNo]: {
        ...prev[axleNo],
        [field]: value,
      },
    }));

    // Clear error for this field when changed
    if (errors[axleNo]?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [axleNo]: {
          ...prev[axleNo],
          [field]: '',
        },
      }));
    }
  };

  const validateForm = (axleNo: number, form: any) => {
    const newErrors: Record<string, string> = {};

    const minP = Number(form.minPressure);
    const maxP = Number(form.maxPressure);

    const minT = Number(form.minTemperature);
    const warnT = Number(form.warningTemperature);
    const maxT = Number(form.maxTemperature);

    if (isNaN(minP) || form.minPressure === '')
      newErrors.minPressure = 'Required';
    if (isNaN(maxP) || form.maxPressure === '')
      newErrors.maxPressure = 'Required';

    if (minP >= maxP) {
      newErrors.minPressure = 'Must be less than Max';
      newErrors.maxPressure = 'Must be greater than Min';
    }

    if (isNaN(minT) || form.minTemperature === '')
      newErrors.minTemperature = 'Required';
    if (isNaN(warnT) || form.warningTemperature === '')
      newErrors.warningTemperature = 'Required';
    if (isNaN(maxT) || form.maxTemperature === '')
      newErrors.maxTemperature = 'Required';

    if (minT >= warnT) {
      newErrors.minTemperature = 'Must be less than Warning';
      newErrors.warningTemperature = 'Must be greater than Min';
    }
    if (warnT >= maxT) {
      newErrors.warningTemperature = 'Must be less than Max';
      newErrors.maxTemperature = 'Must be greater than Warning';
    }
    if (minT >= maxT) {
      newErrors.minTemperature = 'Must be less than Max';
      newErrors.maxTemperature = 'Must be greater than Min';
    }

    setErrors((prev) => ({ ...prev, [axleNo]: newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const onSaveAxle = async (axleNo: number, positions: any[]) => {
    const form = axleForms[axleNo];
    if (!validateForm(axleNo, form)) return;

    const selectedItem = data.find(
      (d) => `${d.make}-${d.model}` === selectedMakeModelPath
    );
    if (!selectedItem) return;

    const rep = positions[0];

    const payload: any = {
      customerId,
      selectedMake: selectedItem.make,
      selectedModel: selectedItem.model,
      axleNo,
      minPressure: Number(form.minPressure),
      maxPressure: Number(form.maxPressure),
      minTemperature: Number(form.minTemperature),
      maxTemperature: Number(form.maxTemperature),
      warningPressure: Number(form.warningPressure),
      warningTemperature: Number(form.warningTemperature),
      oemRecommendedMinPressure: rep.oemRecommendedMinPressure,
      oemRecommendedMaxPressure: rep.oemRecommendedMaxPressure,
    };
    if (!applyToAll && selectedAssetId) {
      payload.assetId = selectedAssetId;
    }

    const success = await saveAxleLimit(payload);
    if (success) {
      setOriginalAxleForms((prev) => ({
        ...prev,
        [axleNo]: { ...form },
      }));
      // Keep `data` in sync so switching make/model and returning shows saved values
      setData((prev) =>
        prev.map((item) => {
          if (`${item.make}-${item.model}` !== selectedMakeModelPath)
            return item;
          return {
            ...item,
            positions: item.positions.map((pos: any) => {
              if (pos.axleNo !== axleNo) return pos;
              const toKpa = (v: string) =>
                unitSystem !== 'metric'
                  ? Math.round(convertPsiToKpa(Number(v)))
                  : Number(v);
              const toC = (v: string) =>
                unitSystem !== 'metric'
                  ? Math.round(convertFahrToCent(Number(v)))
                  : Number(v);
              return {
                ...pos,
                minPressure: toKpa(form.minPressure),
                maxPressure: toKpa(form.maxPressure),
                warningPressure: toKpa(form.warningPressure),
                minTemperature: toC(form.minTemperature),
                maxTemperature: toC(form.maxTemperature),
                warningTemperature: toC(form.warningTemperature),
              };
            }),
          };
        })
      );
    }
  };

  const selectedItem = data.find(
    (d) => `${d.make}-${d.model}` === selectedMakeModelPath
  );
  const axlesGrouped = selectedItem?.positions.reduce((acc: any, curr: any) => {
    if (!acc[curr.axleNo]) acc[curr.axleNo] = [];
    acc[curr.axleNo].push(curr);
    return acc;
  }, {});

  const palette =
    clientThemePalette[CLIENT_THEME][enableDarkTheme ? 'dark' : 'light'];

  // Keep modal styling consistent with TirePressure screens (supports default + Yokohama).
  // NOTE: Yokohama dark `modalBackground` is intentionally reddish; it looks bad
  // for large form dialogs, so we prefer neutral surfaces here.
  const modalBgColor = palette.boxBackground;
  const modalTextColor = palette.textPrimary;
  const modalTitleBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : palette.topBar;
  const modalBorderColor = palette.borderGrey || palette.border;
  const leftPaneBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : palette.toolBarBackground;
  // axle card is slightly darker than modal for depth
  const axleBoxBgColor = enableDarkTheme
    ? palette.boxBackground
    : palette.toolBarBackground;
  const axleHeaderBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : palette.toolBarBackground;
  // input needs to be LIGHTER than the axle card so it's visible
  const inputBgColor = enableDarkTheme
    ? palette.pageBackground
    : palette.boxBackground;
  const sectionLabelColor = enableDarkTheme
    ? palette.textSecondary
    : palette.grey;
  const accentColor = palette.primary;
  const selectedBgColor = enableDarkTheme
    ? `${accentColor}1A` // ~10% opacity (red in dark can look muddy)
    : `${accentColor}26`; // ~15% opacity
  const selectedBorderColor = accentColor;
  const selectedTextColor = accentColor;
  const inactiveWheelColor = enableDarkTheme
    ? palette.grey
    : palette.mediumGray;

  const dialogPaperSx = {
    backgroundColor: modalBgColor,
    color: modalTextColor,
    borderRadius: '12px',
    border: `1px solid ${modalBorderColor}`,
    boxShadow: enableDarkTheme
      ? '0px 18px 50px rgba(0,0,0,0.55)'
      : '0px 18px 50px rgba(15,23,42,0.18)',
  } as const;

  const dialogTitleSx = {
    backgroundColor: modalTitleBgColor,
    borderBottom: `1px solid ${modalBorderColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    fontSize: '1.25rem',
    fontWeight: 500,
  } as const;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: inputBgColor,
      transition: 'border-color 120ms ease, box-shadow 120ms ease',
      color: modalTextColor,
      '& fieldset': {
        borderColor: modalBorderColor,
      },
      '&:hover fieldset': {
        borderColor: selectedBorderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: selectedBorderColor,
        boxShadow: `${selectedBorderColor}33 0px 0px 0px 3px`,
      },
    },
    '& .MuiOutlinedInput-input': {
      color: modalTextColor,
      fontWeight: 600,
      fontSize: '16px',
      letterSpacing: '0.2px',
    },
    '& .MuiInputLabel-root': {
      color: sectionLabelColor,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: selectedTextColor,
    },
  } as const;

  const formatPositionLabel = (raw: string): string => {
    const cleaned = raw.replace(/_/g, ' ');
    const parts = cleaned.split(/[\s-]+/).filter(Boolean);
    return parts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const AxleSchematic = ({
    currentAxle,
    totalAxles,
    totalWheels,
    patternName,
  }: {
    currentAxle: number;
    totalAxles: number;
    totalWheels: number;
    patternName?: string;
  }) => {
    const wheelColor = accentColor;
    const inactiveColor = inactiveWheelColor;

    const sxOverride = {
      '& rect': {
        fill: `${inactiveColor} !important`,
        transition: 'fill 200ms ease',
      },
      '& .selected-wheel rect': {
        fill: `${wheelColor} !important`,
      },
    };

    const tireStatusMap: Record<string, TireStatus> = {};
    const mockValues: any = {};

    // We pass generic classes down to specific tires if possible, but the specific components
    // don't take classes per tire.
    // However, they DO apply fills based on `tireStatusMap`.
    // Let's use `tireStatusMap` to set them to `HEALTHY` (selected) and `NO_SENSOR` (inactive).
    // And then use CSS to specifically target the resulting colors.
    // getTpmsTireColor HEALTHY = #27AE60, NO_SENSOR = #7D7D7D

    const overrideByColors = {
      ...sxOverride,
      '& rect[fill="#27AE60"], & rect[fill="#155d33"]': {
        fill: `${wheelColor} !important`,
      },
      '& rect[fill="#7D7D7D"]': {
        fill: `${inactiveColor} !important`,
      },
    };

    if (patternName === '4x6') {
      // Reach stacker: axle 1 = 4 dual front tires, axle 2 = 2 single rear
      // tires — same (totalAxles=2, totalWheels=6) as '6x4' but the OPPOSITE
      // axle distribution, so this must be checked before the generic
      // totalAxles/totalWheels bucket below.
      tireStatusMap.p1 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p2 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p3 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p4 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p5 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p6 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            ...overrideByColors,
          }}
        >
          <Box
            sx={{ transform: 'scale(0.8)', transformOrigin: 'center center' }}
          >
            <FourSixWheels values={mockValues} tireStatusMap={tireStatusMap} />
          </Box>
        </Box>
      );
    } else if (totalAxles === 2 && totalWheels === 4) {
      tireStatusMap.p1 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p2 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p3 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p4 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            ...overrideByColors,
          }}
        >
          <Box
            sx={{ transform: 'scale(1.2)', transformOrigin: 'center center' }}
          >
            <FourWheels values={mockValues} tireStatusMap={tireStatusMap} />
          </Box>
        </Box>
      );
    } else if (totalAxles === 2 && totalWheels === 6) {
      tireStatusMap.p1 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p2 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p3 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p4 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p5 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p6 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            ...overrideByColors,
          }}
        >
          <Box
            sx={{ transform: 'scale(0.8)', transformOrigin: 'center center' }}
          >
            <SixWheels values={mockValues} tireStatusMap={tireStatusMap} />
          </Box>
        </Box>
      );
    } else if (totalAxles === 3 && totalWheels === 10) {
      tireStatusMap.p1 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p2 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p3 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p4 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p5 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p6 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p7 =
        currentAxle === 3 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p8 =
        currentAxle === 3 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p9 =
        currentAxle === 3 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p10 =
        currentAxle === 3 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            ...overrideByColors,
          }}
        >
          <Box
            sx={{ transform: 'scale(0.7)', transformOrigin: 'center center' }}
          >
            <ThreeAxlesTenWheels
              values={mockValues}
              tireStatusMap={tireStatusMap}
            />
          </Box>
        </Box>
      );
    } else if (totalAxles === 3 && totalWheels === 6) {
      tireStatusMap.p1 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p2 =
        currentAxle === 1 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p3 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p4 =
        currentAxle === 2 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p5 =
        currentAxle === 3 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      tireStatusMap.p6 =
        currentAxle === 3 ? TireStatus.HEALTHY : TireStatus.NO_SENSOR;
      return (
        <Box
          sx={{
            width: '100%',
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            ...overrideByColors,
          }}
        >
          <Box
            sx={{ transform: 'scale(1.2)', transformOrigin: 'center center' }}
          >
            <ThreeAxlesSixWheels
              values={mockValues}
              tireStatusMap={tireStatusMap}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          height: '180px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          ...overrideByColors,
        }}
      >
        <Box sx={{ transform: 'scale(1.2)', transformOrigin: 'center center' }}>
          <FourWheels values={mockValues} tireStatusMap={tireStatusMap} />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={attemptClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <Typography variant="h6" component="div">
            {t('set_axle_wise_thresholds')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={attemptClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 0,
            display: 'flex',
            height: { xs: '75vh', md: '70vh' },
          }}
        >
          {/* Left Pane: Make / Models */}
          <Box
            sx={{
              width: { xs: 220, md: 260 },
              borderRight: `1px solid ${modalBorderColor}`,
              backgroundColor: leftPaneBgColor,
              overflowY: 'auto',
            }}
          >
            {data.map((item) => {
              const p = `${item.make}-${item.model}`;
              const makeModelAssets = assets.filter(
                (a) =>
                  a.make === item.make &&
                  a.model === item.model &&
                  a.bumperNumber
              );
              return (
                <Box key={p}>
                  {/* Make/Model group header */}
                  <Typography
                    sx={{
                      padding: '6px 16px',
                      color: sectionLabelColor,
                      fontSize: '11px',
                      fontWeight: 'bold',
                      letterSpacing: '0.5px',
                      opacity: 0.7,
                    }}
                  >
                    {item.make} {item.model}
                  </Typography>
                  {/* Individual asset rows */}
                  {makeModelAssets.map((a) => {
                    const isSelected =
                      p === selectedMakeModelPath &&
                      selectedAssetId === a.assetId;
                    return (
                      <Box
                        key={a.assetId}
                        onClick={() => {
                          setSelectedAssetId(a.assetId);
                          setApplyToAll(false);
                          handleSelectMakeModel(item);
                          void fetchLimits(a.assetId);
                        }}
                        sx={{
                          padding: '10px 16px',
                          margin: '0 8px 4px 8px',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          transition:
                            'background-color 120ms ease, border-color 120ms ease',
                          backgroundColor: isSelected
                            ? selectedBgColor
                            : 'transparent',
                          border: isSelected
                            ? `1px solid ${selectedBorderColor}`
                            : '1px solid transparent',
                          color: isSelected
                            ? selectedTextColor
                            : modalTextColor,
                          '&:hover': {
                            backgroundColor: isSelected
                              ? selectedBgColor
                              : `${accentColor}14`,
                            borderColor: isSelected
                              ? selectedBorderColor
                              : `${accentColor}55`,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}
                        >
                          {a.bumperNumber}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          {/* Right Pane: Properties */}
          <Box
            sx={{
              flex: 1,
              padding: '24px',
              backgroundColor: enableDarkTheme
                ? palette.pageBackground
                : modalBgColor,
              overflowY: 'auto',
            }}
          >
            {selectedItem && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: modalTextColor,
                    }}
                  >
                    {selectedItem.make} {selectedItem.model}
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedItem.patternName}
                    sx={{
                      backgroundColor: selectedBgColor,
                      color: selectedTextColor,
                      border: `1px solid ${selectedBorderColor}`,
                      fontWeight: 700,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: sectionLabelColor }}>
                    {selectedItem.totalAxles} axles
                  </Typography>
                </Box>

                {/* Apply-to-all checkbox */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '24px',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={applyToAll}
                        onChange={(e) => {
                          setApplyToAll(e.target.checked);
                        }}
                        sx={{
                          color: accentColor,
                          '&.Mui-checked': { color: accentColor },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{ color: modalTextColor, fontSize: '14px' }}
                      >
                        Apply to all {selectedItem.make} {selectedItem.model}
                      </Typography>
                    }
                  />
                </Box>

                {Object.keys(axlesGrouped || {}).map((axleNoStr) => {
                  const axleNo = Number(axleNoStr);
                  const positions = axlesGrouped[axleNo];
                  const labels = positions
                    .map((p: any) => formatPositionLabel(p.tireCodeLabel))
                    .join(', ');
                  const oemMin = positions[0].oemRecommendedMinPressure;
                  const oemMax = positions[0].oemRecommendedMaxPressure;
                  const oemStr = `OEM: ${
                    unitSystem !== 'metric'
                      ? Math.round(convertKpaToPsi(oemMin))
                      : oemMin
                  }-${
                    unitSystem !== 'metric'
                      ? Math.round(convertKpaToPsi(oemMax))
                      : oemMax
                  } ${unitSystem !== 'metric' ? 'PSI' : 'KPA'}`;

                  const form = axleForms[axleNo] || {};

                  return (
                    <Box
                      key={axleNo}
                      sx={{
                        border: `1px solid ${modalBorderColor}`,
                        borderRadius: '8px',
                        marginBottom: '24px',
                        overflow: 'hidden',
                        backgroundColor: axleBoxBgColor,
                      }}
                    >
                      <Box
                        sx={{
                          padding: '12px 16px',
                          borderBottom: `1px solid ${modalBorderColor}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: axleHeaderBgColor,
                          gap: '12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            minWidth: 240,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              color: modalTextColor,
                            }}
                          >
                            Axle {axleNo}
                          </Typography>
                          <Typography
                            sx={{
                              color: sectionLabelColor,
                              fontSize: '14px',
                            }}
                          >
                            {labels}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                          }}
                        >
                          <Typography
                            sx={{
                              color: sectionLabelColor,
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {oemStr}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              void onSaveAxle(axleNo, positions);
                            }}
                            sx={{
                              borderColor: modalBorderColor,
                              color: modalTextColor,
                              textTransform: 'none',
                              borderRadius: '10px',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: selectedBgColor,
                                borderColor: selectedBorderColor,
                              },
                            }}
                          >
                            {t('save_axle')}
                          </Button>
                        </Box>
                      </Box>

                      <Box sx={{ padding: '24px' }}>
                        <Grid container spacing={3} alignItems="center">
                          {/* Left: schematic */}
                          <Grid item xs={12} md={3}>
                            <AxleSchematic
                              currentAxle={axleNo}
                              totalAxles={selectedItem.totalAxles}
                              totalWheels={selectedItem.positions.length}
                              patternName={selectedItem.patternName}
                            />
                          </Grid>
                          {/* Right: stacked pressure + temperature */}
                          <Grid item xs={12} md={9}>
                            {/* Pressure Section */}
                            <Typography
                              sx={{
                                color: sectionLabelColor,
                                fontSize: '12px',
                                fontWeight: 'bold',
                                marginBottom: '16px',
                                letterSpacing: '1px',
                              }}
                            >
                              {t('pressure_2')}
                              {unitSystem !== 'metric' ? 'PSI' : 'KPA'})
                            </Typography>
                            <Grid
                              container
                              spacing={3}
                              sx={{ marginBottom: '32px' }}
                            >
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label={t('min_pressure')}
                                  size="small"
                                  value={form.minPressure ?? ''}
                                  onChange={(e) => {
                                    handleFieldChange(
                                      axleNo,
                                      'minPressure',
                                      e.target.value
                                    );
                                  }}
                                  error={!!errors[axleNo]?.minPressure}
                                  helperText={errors[axleNo]?.minPressure}
                                  InputLabelProps={{ shrink: true }}
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label={t('max_pressure')}
                                  size="small"
                                  value={form.maxPressure ?? ''}
                                  onChange={(e) => {
                                    handleFieldChange(
                                      axleNo,
                                      'maxPressure',
                                      e.target.value
                                    );
                                  }}
                                  error={!!errors[axleNo]?.maxPressure}
                                  helperText={errors[axleNo]?.maxPressure}
                                  InputLabelProps={{ shrink: true }}
                                  sx={inputSx}
                                />
                              </Grid>
                            </Grid>

                            {/* Temperature Section */}
                            <Typography
                              sx={{
                                color: sectionLabelColor,
                                fontSize: '12px',
                                fontWeight: 'bold',
                                marginBottom: '16px',
                                letterSpacing: '1px',
                              }}
                            >
                              {t('temperature')}
                              {unitSystem !== 'metric' ? '°F' : '°C'})
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label={t('min_temperature')}
                                  size="small"
                                  value={form.minTemperature ?? ''}
                                  onChange={(e) => {
                                    handleFieldChange(
                                      axleNo,
                                      'minTemperature',
                                      e.target.value
                                    );
                                  }}
                                  error={!!errors[axleNo]?.minTemperature}
                                  helperText={errors[axleNo]?.minTemperature}
                                  InputLabelProps={{ shrink: true }}
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label={t('warning_temperature')}
                                  size="small"
                                  value={form.warningTemperature ?? ''}
                                  onChange={(e) => {
                                    handleFieldChange(
                                      axleNo,
                                      'warningTemperature',
                                      e.target.value
                                    );
                                  }}
                                  error={!!errors[axleNo]?.warningTemperature}
                                  helperText={
                                    errors[axleNo]?.warningTemperature
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  sx={inputSx}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label={t('max_temperature')}
                                  size="small"
                                  value={form.maxTemperature ?? ''}
                                  onChange={(e) => {
                                    handleFieldChange(
                                      axleNo,
                                      'maxTemperature',
                                      e.target.value
                                    );
                                  }}
                                  error={!!errors[axleNo]?.maxTemperature}
                                  helperText={errors[axleNo]?.maxTemperature}
                                  InputLabelProps={{ shrink: true }}
                                  sx={inputSx}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => {
          setConfirmDialog({ open: false, nextAction: () => {} });
        }}
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <Typography variant="h6" component="div">
            {t('unsaved_changes')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => {
              setConfirmDialog({ open: false, nextAction: () => {} });
            }}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '16px 24px' }}>
          <Typography sx={{ color: modalTextColor }}>
            {t('you_have_unsaved_changes_are_you_sure_you_want_to_continue')}
          </Typography>
          <Divider sx={{ marginTop: '16px', borderColor: modalBorderColor }} />
        </DialogContent>
        <DialogActions sx={{ padding: '12px 16px' }}>
          <Button
            onClick={() => {
              setConfirmDialog({ open: false, nextAction: () => {} });
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: modalBorderColor,
              color: modalTextColor,
              '&:hover': {
                backgroundColor: `${accentColor}14`,
                borderColor: selectedBorderColor,
              },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmDialog.nextAction}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              backgroundColor: accentColor,
              '&:hover': {
                backgroundColor: palette.primaryDark,
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
